import { HttpClient, robotoHeaders, type PaginatedResponse } from "../../http";

import {
  type TriggerEvaluationRecord,
  type TriggerEvaluationSummary,
} from "./TriggerEvaluationRecord";

type Options = {
  abortSignal: AbortSignal;
  resourceOwnerId: string;
};

/**
 * The trigger routes moved to a new wire shape at API version 2026-08-27, and
 * the evaluation routes answer callers pinned before that version from a
 * different source than callers after it. Every request this service makes is
 * pinned to the last version before the move; changing the pin changes what
 * the evaluation routes return.
 */
const LEGACY_TRIGGER_API_VERSION = "2026-08-10";

/**
 * A dataset's trigger evaluations: the paged list, their count, and a summary
 * of the pending ones.
 *
 * @deprecated Calls legacy trigger-evaluation endpoints that have no
 * equivalent in the trigger API. It remains only for the dataset file
 * browser's trigger evaluation status, and will be removed once that view is
 * rebuilt on trigger dispatches or a backend summary endpoint exists.
 */
export class TriggerEvaluationService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getPagedTriggerEvaluationsForDataset(
    datasetId: string,
    options?: Partial<
      Options & {
        limit: number;
        pageToken: string;
      }
    >,
  ): Promise<PaginatedResponse<TriggerEvaluationRecord>> {
    const { abortSignal, limit, pageToken, resourceOwnerId } = options ?? {};
    const searchParams = new URLSearchParams();
    if (limit !== undefined) {
      searchParams.append("limit", limit.toString());
    }
    if (pageToken) {
      searchParams.append("page_token", pageToken);
    }

    const url = this.#httpClient.constructUrl(
      `v1/triggers/dataset/id/${datasetId}/evaluations`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      signal: abortSignal,
      headers: robotoHeaders({
        apiVersion: LEGACY_TRIGGER_API_VERSION,
        resourceOwnerId,
      }),
    });
    return response.json<PaginatedResponse<TriggerEvaluationRecord>>();
  }

  public async getTriggerEvaluationCountForDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<number> {
    const url = this.#httpClient.constructUrl(
      `v1/datasets/${datasetId}/trigger-evaluations/count`,
    );

    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        apiVersion: LEGACY_TRIGGER_API_VERSION,
        resourceOwnerId: options?.resourceOwnerId,
      }),
    });

    return Number.parseInt(await response.text());
  }

  public async getTriggerEvaluationsSummaryForDataset(
    datasetId: string,
    options?: Partial<Options>,
  ): Promise<TriggerEvaluationSummary> {
    const url = this.#httpClient.constructUrl(
      `v1/triggers/dataset/id/${datasetId}/evaluations/summary`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: robotoHeaders({
        apiVersion: LEGACY_TRIGGER_API_VERSION,
        resourceOwnerId: options?.resourceOwnerId,
      }),
    });
    return response.json<TriggerEvaluationSummary>();
  }
}
